import { useState, useMemo } from 'react';
import { scoreChallenge } from '../../../services/rebusSessionService';
import { recalculateTeamScore } from '../../../services/rebusTeamService';

export default function StationLeaderView({ session, rebus, stationId }) {
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [errors, setErrors] = useState({});

  // Find the station data from the rebus
  const station = useMemo(() => {
    if (!rebus?.stations) return null;
    return rebus.stations.find((s) => s.id === stationId);
  }, [rebus, stationId]);

  // Get all teams from the session
  const teams = useMemo(() => {
    if (!session?.teams) return [];
    return Object.entries(session.teams).map(([id, data]) => ({
      id,
      ...data
    }));
  }, [session]);

  // Get the station leader name
  const leaderName = useMemo(() => {
    return session?.stationLeaders?.[stationId]?.name || 'Ukjend';
  }, [session, stationId]);

  // Check if a team has already been scored at this station
  const getExistingScore = (teamId) => {
    const result = session?.teams?.[teamId]?.stationResults?.[stationId];
    if (result && result.challengeScore !== undefined) {
      return result;
    }
    return null;
  };

  const handleScoreChange = (teamId, value) => {
    const maxPoints = station?.challenge?.maxPoints || 10;
    let numVal = value === '' ? '' : parseInt(value, 10);
    if (numVal !== '' && !isNaN(numVal)) {
      numVal = Math.max(0, Math.min(numVal, maxPoints));
    }
    setScores((prev) => ({ ...prev, [teamId]: numVal }));
    setErrors((prev) => ({ ...prev, [teamId]: null }));
  };

  const handleSaveScore = async (teamId) => {
    const score = scores[teamId];
    if (score === '' || score === undefined || isNaN(score)) {
      setErrors((prev) => ({ ...prev, [teamId]: 'Skriv inn eit gyldig tal' }));
      return;
    }

    setSaving((prev) => ({ ...prev, [teamId]: true }));
    setErrors((prev) => ({ ...prev, [teamId]: null }));

    try {
      await scoreChallenge(session.id, teamId, stationId, score, leaderName);
      await recalculateTeamScore(session.id, teamId);
      setSaved((prev) => ({ ...prev, [teamId]: true }));
      setTimeout(() => {
        setSaved((prev) => ({ ...prev, [teamId]: false }));
      }, 2000);
    } catch (err) {
      setErrors((prev) => ({ ...prev, [teamId]: err.message }));
    } finally {
      setSaving((prev) => ({ ...prev, [teamId]: false }));
    }
  };

  if (!station) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
          <span className="material-symbols-outlined text-4xl text-red-500 mb-2">error</span>
          <p className="text-gray-700 font-semibold">Fann ikkje stasjonen</p>
        </div>
      </div>
    );
  }

  const maxPoints = station.challenge?.maxPoints || 10;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-teal-600 text-white px-4 py-5 shadow-md">
        <div className="max-w-lg mx-auto">
          <p className="text-teal-200 text-xs font-bold tracking-widest uppercase mb-1">Stasjonsleiar</p>
          <h1 className="text-xl font-bold">
            Stasjon {station.order}: {station.name}
          </h1>
          <p className="text-teal-100 text-sm mt-1">{leaderName}</p>
        </div>
      </header>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Challenge Card */}
        {station.challenge && (
          <div className="bg-white rounded-2xl shadow-ambient p-5 border-l-4 border-teal-500">
            <div className="flex items-start gap-3 mb-3">
              <span className="material-symbols-outlined text-teal-600 text-2xl mt-0.5" style={{ fontVariationSettings: "'FILL' 1" }}>
                emoji_events
              </span>
              <div>
                <h2 className="font-bold text-gray-900 text-lg">{station.challenge.title}</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {station.challenge.scoringType === 'time' ? 'Tidsbasert' : 'Poengbasert'} — maks {maxPoints} poeng
                </p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed">{station.challenge.description}</p>
          </div>
        )}

        {/* Team Scoring */}
        <div className="bg-white rounded-2xl shadow-ambient overflow-hidden">
          <div className="bg-gray-50 px-5 py-3">
            <h2 className="font-bold text-gray-800 flex items-center gap-2">
              <span className="material-symbols-outlined text-xl">groups</span>
              Lag ({teams.length})
            </h2>
          </div>

          {teams.length === 0 ? (
            <div className="p-6 text-center text-gray-400">
              <span className="material-symbols-outlined text-4xl mb-2">hourglass_empty</span>
              <p>Ingen lag har blitt med enno</p>
            </div>
          ) : (
            <div className="space-y-2">
              {teams.map((team) => {
                const existing = getExistingScore(team.id);
                const isSaving = saving[team.id];
                const justSaved = saved[team.id];
                const error = errors[team.id];

                return (
                  <div key={team.id} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900 text-lg">{team.name || team.id}</span>
                      {existing && (
                        <span className="flex items-center gap-1 text-green-600 font-bold text-sm">
                          <span className="material-symbols-outlined text-lg" style={{ fontVariationSettings: "'FILL' 1" }}>
                            check_circle
                          </span>
                          {existing.challengeScore} poeng
                        </span>
                      )}
                    </div>

                    {existing ? (
                      <p className="text-gray-400 text-sm">
                        Scora av {existing.challengeScoredBy}
                      </p>
                    ) : (
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex-1">
                          <input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            max={maxPoints}
                            placeholder={`0 - ${maxPoints}`}
                            value={scores[team.id] ?? ''}
                            onChange={(e) => handleScoreChange(team.id, e.target.value)}
                            className="w-full px-4 py-3 text-lg font-semibold ghost-border ghost-border-focus rounded-xl text-center"
                            disabled={isSaving}
                          />
                        </div>
                        <button
                          onClick={() => handleSaveScore(team.id)}
                          disabled={isSaving || scores[team.id] === '' || scores[team.id] === undefined}
                          className={`px-5 py-3 rounded-xl font-bold text-white text-base transition-all ${
                            justSaved
                              ? 'bg-green-500'
                              : isSaving
                                ? 'bg-teal-400 cursor-wait'
                                : 'bg-gradient-to-r from-eq-primary to-eq-primary-container hover:brightness-110 active:scale-95'
                          } disabled:opacity-40 disabled:cursor-not-allowed`}
                        >
                          {justSaved ? (
                            <span className="material-symbols-outlined text-xl" style={{ fontVariationSettings: "'FILL' 1" }}>check</span>
                          ) : isSaving ? (
                            <span className="material-symbols-outlined text-xl animate-spin">progress_activity</span>
                          ) : (
                            'Lagre'
                          )}
                        </button>
                      </div>
                    )}

                    {error && (
                      <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">error</span>
                        {error}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Station Questions (read-only reference) */}
        {station.questions && station.questions.length > 0 && (
          <details className="bg-white rounded-2xl shadow-ambient overflow-hidden">
            <summary className="px-5 py-4 cursor-pointer font-bold text-gray-700 flex items-center gap-2 hover:bg-gray-50 transition-colors">
              <span className="material-symbols-outlined text-xl text-teal-600">help</span>
              Sporsmal og svar ({station.questions.length})
            </summary>
            <div className="space-y-2 pt-2">
              {station.questions.map((q, idx) => (
                <div key={q.id} className="px-5 py-3">
                  <p className="font-medium text-gray-800 text-sm">
                    {idx + 1}. {q.text}
                  </p>
                  <p className="text-teal-700 text-sm mt-1 font-semibold">
                    Svar:{' '}
                    {q.type === 'multiple_choice'
                      ? q.answers?.find((a) => a.isCorrect)?.text || '—'
                      : q.correctAnswer || '—'}
                  </p>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Session status */}
        <div className="text-center text-gray-400 text-xs py-4">
          Rebus-kode: {session?.id} | Status: {session?.status}
        </div>
      </div>
    </div>
  );
}
